import type { Event } from "./types";
import { getEventGenres, groupRecurringEvents } from "./utils";
import { CONCERT_GENRES, matchesConcertGenreTags, type ConcertGenre } from "./genre-taxonomy";
import { isEventInFeed } from "./event-expiry";

export * from "./genre-taxonomy";

export function matchesConcertGenre(event: Event, genre: ConcertGenre): boolean {
  return matchesConcertGenreTags(getEventGenres(event), genre);
}

export function cityLocalDate(timezone: string, now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

export function addCalendarDays(date: string, days: number): string {
  const value = new Date(`${date}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

export function isUpcomingConcertEvent(event: Event, today: string, now = new Date()): boolean {
  if (!event.local_date || /^(cancelled|canceled)$/i.test(event.status ?? "")) return false;
  // Feed RPCs normalize timed shows to an absolute start, even if their display
  // date belongs to the previous evening. Unknown start times keep the day rule.
  return event.start_datetime ? isEventInFeed(event, "UTC", now) : event.local_date >= today;
}

export function upcomingConcertEvents(events: Event[], today: string, now = new Date()): Event[] {
  const seen = new Set<number>();
  return events.filter((event) => {
    if (seen.has(event.id) || !isUpcomingConcertEvent(event, today, now)) return false;
    seen.add(event.id);
    return true;
  }).sort((a, b) => (a.local_date ?? "").localeCompare(b.local_date ?? "") || (a.local_time ?? "").localeCompare(b.local_time ?? "") || a.id - b.id);
}

export interface CityGenreSummary {
  slug: string;
  eventCount: number;
  billCount: number;
  venueCount: number;
  nearTermCount: number;
  indexable: boolean;
}

export function summarizeCityGenres(events: Event[], today: string, now = new Date()): CityGenreSummary[] {
  const end = addCalendarDays(today, 90);
  const nearEnd = addCalendarDays(today, 30);
  const upcoming = upcomingConcertEvents(events, today, now).filter((event) => event.local_date! < end);
  return CONCERT_GENRES.map((genre) => {
    const matching = upcoming.filter((event) => matchesConcertGenre(event, genre));
    const billCount = groupRecurringEvents(matching).length;
    const venueCount = new Set(matching.map((event) => event.venue.id)).size;
    const nearTermCount = matching.filter((event) => event.local_date! < nearEnd).length;
    return { slug: genre.slug, eventCount: matching.length, billCount, venueCount, nearTermCount, indexable: billCount >= 10 && venueCount >= 3 && nearTermCount > 0 };
  });
}
